import { Component, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import {
  ActionSheetController,
  AlertController,
  Gesture,
  GestureController,
  ModalController,
  NavController,
  Platform,
} from '@ionic/angular';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Location } from '@angular/common';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { ConfigService } from '@smatchy/ionic-core';
import { LanguageService } from './core/services/language.service';
import { ExploreMapUiService } from './core/services/explore-map-ui.service';
import { NavigationRedirectService } from './core/services/navigation-redirect.service';
import { HOME_PATH } from './presentations/pages/logged/pages/home/home.routing.module';
import { transformLegacyPath } from './core/utils/route-transform.utils';
import { keepTheCodeOfALink, whatALinkCarries } from './core/domains/social/a-referral-code-arrives-by-a-link';
import { OfflineContainerComponent } from './presentations/components/design-system/organisms/offline-container/offline-container.component';
import { theScreenSaysItsOwnError } from './core/utils/a-screen-with-its-own-error-keeps-it-offline';
import { TranslateService } from '@ngx-translate/core';
import { filter, Subscription, firstValueFrom } from 'rxjs';
import { JWTService } from '@smatchy/ionic-auth';
import { Browser } from '@capacitor/browser';
import { Store } from '@ngxs/store';
import { PushNotificationAdapter } from './adapters/push-notification.adapter';
import { LegacyPasswordPurge } from './core/services/legacy-password-purge.service';
import { EDGE_WIDTH_PX, isATabRoot, isBackSwipe, isForwardSwipe } from './core/navigation/edge-swipe';
import { backTarget } from './core/navigation/back-target';
import { NavigationHistoryService } from './core/navigation/navigation-history.service';
import { UsageMeasurementService } from './core/services/usage-measurement.service';
import { MinimumVersionService } from './core/domains/app/minimum-version.service';

@Component({
  selector: 'smatchy-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private offlineModal?: HTMLIonModalElement;
  private offlineModalIsOpen = false;
  private routerSubscription: Subscription;
  private backButtonSubscription: Subscription;
  private navigationHistory: string[] = [];
  private isHandlingBackGesture = false;
  private hasTrackedFirstNavigation = false;
  private readonly lastRouteStorageKey = 'lastRoute';
  private forwardSwipeGesture?: Gesture;
  private backSwipeGesture?: Gesture;

  private readonly config: ConfigService = inject(ConfigService);

  constructor(
    public readonly _platform: Platform,
    private readonly _router: Router,
    private readonly _location: Location,
    private readonly _modalCtrl: ModalController,
    private readonly _navCtrl: NavController,
    private readonly _actionSheetCtrl: ActionSheetController,
    private readonly zone: NgZone,
    private readonly languageService: LanguageService,
    private readonly alertController: AlertController,
    private readonly translate: TranslateService,
    private readonly _jwtService: JWTService,
    private readonly _navigationRedirectService: NavigationRedirectService,
    private readonly _exploreMapUiService: ExploreMapUiService,
    private readonly store: Store,
    private readonly _pushNotificationAdapter: PushNotificationAdapter,
    private readonly _legacyPasswordPurge: LegacyPasswordPurge,
    private readonly _gestureCtrl: GestureController,
    private readonly _navigationHistory: NavigationHistoryService,
    private readonly _usageMeasurement: UsageMeasurementService,
    private readonly _minimumVersion: MinimumVersionService,
  ) {
    this.initializeApp();
    this.setupBackButtonHandler();
  }

  ngOnInit(): void {
    // First thing, before anything can read it. Every release since 2.0.1 wrote
    // the account password into localStorage, 2.1.4 included, and the entry was
    // only ever cleared on a completed onboarding or a later sign-in. Anyone who
    // gave up part-way still carries theirs. Not writing any more would only
    // have been half a fix.
    this._legacyPasswordPurge.purgeLegacyStorage();

    // The refusal of the usage measurement, applied on every run. The SDK
    // remembers the setting by itself, but a build that never calls it leaves a
    // device that refused collecting for ever, which is the state this
    // application was in from 12 August 2026 until this line existed. Never
    // awaited and never able to throw: nothing a member asked for waits on a
    // measurement call.
    void this._usageMeasurement.applyTheStoredChoice().catch((error: unknown) => {
      // The service swallows its own failures, so this catch should be dead
      // code. It is here because a promise started with `void` and no catch is
      // an unhandled rejection the day that stops being true, and an unhandled
      // rejection in this repository has already taken a whole test run down.
      console.warn('[Analytics] the stored measurement choice was not applied', error);
    });

    this._setupOffline();
    this.setupMinimumVersion();
    this.setupForwardSwipe();
    this.setupBackSwipe();
    this.setupNavigationTracking();
    this.setupPopstateHandler();
    this.setupPushNotificationListener();

    document.addEventListener('deviceready', () => {
      setTimeout(async () => {
        await SplashScreen.hide();
      }, 400);
    });
  }

  /**
   * The update screen, for a copy of the application the API no longer
   * accepts. Asked on start and every time the application comes back to the
   * front, which is when a member returns from the store. Never awaited: the
   * service swallows its own failures, and no answer means the application
   * opens as usual.
   */
  private setupMinimumVersion() {
    void this._minimumVersion.check();
    if (Capacitor.isNativePlatform()) {
      void App.addListener('resume', () => void this._minimumVersion.check());
    }
  }

  private setupPushNotificationListener() {
    if (Capacitor.isNativePlatform()) {
      void this._pushNotificationAdapter.addActionPerformedListener();
    }
  }

  private setupNavigationTracking() {
    this.navigationHistory.push(this._router.url);
    this.routerSubscription = this._router.events
      // A type predicate: `instanceof` in a plain boolean narrows inside the
      // arrow and nothing outside it, so the subscriber had to re-declare what
      // it receives -- and Angular's Event union does not accept that
      // annotation. Said once, in the filter.
      .pipe(
        filter(
          (event): event is NavigationStart | NavigationEnd =>
            event instanceof NavigationStart || event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          if (event.navigationTrigger === 'imperative') {
            this.navigationHistory.push(event.url);
          } else if (event.navigationTrigger === 'popstate') {
            this.navigationHistory.pop();
          }
          return;
        }

        // Leaving a screen that speaks for itself while offline brings the
        // offline cover back on the next one, and the other way round.
        void this.updateNetworkStatus();

        if (this.shouldSaveLastRoute(event.urlAfterRedirects)) {
          localStorage.setItem(this.lastRouteStorageKey, event.urlAfterRedirects);
        }
      });
  }

  private shouldSaveLastRoute(url: string): boolean {
    if (!url?.startsWith('/home')) {
      return false;
    }

    const lastRoute = localStorage.getItem(this.lastRouteStorageKey);
    const isDefaultRoute = url === '/home/explore' || url.startsWith('/home/explore?');
    const shouldIgnoreFirstDefaultRoute =
      !this.hasTrackedFirstNavigation && isDefaultRoute && !!lastRoute && lastRoute !== url;

    this.hasTrackedFirstNavigation = true;
    return !shouldIgnoreFirstDefaultRoute;
  }

  private setupPopstateHandler() {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      window.addEventListener('popstate', async () => {
        if (this.isHandlingBackGesture) {
          return;
        }
        this.isHandlingBackGesture = true;
        const previousUrl =
          this.navigationHistory.length > 0 ? this.navigationHistory[this.navigationHistory.length - 1] : '';
        const wasOnHomePage = previousUrl === '/home/explore' || previousUrl.startsWith('/home/explore?');
        await this.zone.run(async () => {
          try {
            if (await this.hasOpenOverlay()) {
              window.history.pushState(null, '', window.location.href);
              return;
            }
            if (this._exploreMapUiService.isExpandedMapOpen()) {
              window.history.pushState(null, '', window.location.href);
              this._exploreMapUiService.requestCloseExpandedMap();
              return;
            }
            if (this._navigationRedirectService.shouldRedirectToExploreOnBack()) {
              window.history.pushState(null, '', window.location.href);
              await this._navigationRedirectService.handleBackNavigationFromGroupDetail(true);
              return;
            }
            if (wasOnHomePage) {
              window.history.pushState(null, '', window.location.href);
              await this.exitApp();
              return;
            }
          } finally {
            setTimeout(() => {
              this.isHandlingBackGesture = false;
            }, 100);
          }
        });
      });
    }
  }

  private async initializeApp() {
    await this.languageService.initStorage();
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(async () => {
        try {
          console.log('appUrlOpen event.url:', event.url);

          // Normalize URL - handle escaped slashes in logs
          const normalizedUrl = event.url.replace(/\\\//g, '/');
          console.log('Normalized URL:', normalizedUrl);

          // Handle Stripe Connect return URLs FIRST - before any other processing
          // Using custom URL scheme: smatchy://stripe-connect/return?account_id=xxx
          if (normalizedUrl.includes('stripe-connect/return') || normalizedUrl.includes('stripe-connect/refresh')) {
            console.log('Stripe Connect return detected (custom URL scheme):', normalizedUrl);

            // Extract account_id from custom URL scheme using regex
            // Format: smatchy://stripe-connect/return?account_id=xxx
            const accountIdMatch = normalizedUrl.match(/account_id=([^&?#]+)/);
            const accountId = accountIdMatch && accountIdMatch[1] ? decodeURIComponent(accountIdMatch[1]) : null;

            if (accountId) {
              console.log('Stripe Connect return - accountId:', accountId);

              // Close the browser if it's open
              try {
                await Browser.close();
                console.log('Browser closed successfully');
              } catch (error) {
                console.log('Browser close error (might not be open):', error);
              }

              // Navigate to onboarding page
              try {
                await this._router.navigate(['/home/profile/stripe-connect-onboarding'], {
                  queryParams: { account_id: accountId },
                });
                console.log('Navigated to stripe-connect-onboarding page with account_id:', accountId);
              } catch (navError) {
                console.error('Navigation error:', navError);
                // Still return to prevent further processing
              }

              // IMPORTANT: Return early to prevent further processing
              return;
            } else {
              console.warn('Stripe Connect return detected but no account_id found in URL:', normalizedUrl);
              // Still return to prevent further processing
              return;
            }
          }

          const url = new URL(event.url);
          let slug = url.pathname;
          const organizerId = url.searchParams.get('organizerId');

          // `?? slug`: `has` and `get` are two reads of the same parameter, and
          // `get` answers null on its own terms. Keeping the path already read
          // is what the branch means when there is nothing to replace it with.
          if (url.searchParams.has('path')) {
            slug = url.searchParams.get('path') ?? slug;
          }

          if (slug.startsWith('/open/')) {
            slug = slug.substring(5);
          }

          slug = transformLegacyPath(slug);
          console.log('Final slug:', slug);

          // Build query params object (excluding internal 'path')
          const queryParams: Record<string, string> = {};
          url.searchParams.forEach((value, key) => {
            if (key !== 'path') {
              queryParams[key] = value;
            }
          });

          queryParams['deepLinkNonce'] = Date.now().toString();

          const isLoggedIn = await firstValueFrom(this._jwtService.isLoggedIn());

          // A referral code is kept for the field of the onboarding (Asana
          // 1219743222744698). Kept even for somebody logged in: the account
          // exists from the password on, so the onboarding may still be ahead.
          const carried = whatALinkCarries(url);
          if (carried) {
            keepTheCodeOfALink(carried.code);
            if (carried.ownLink) {
              // The referral link has no screen of its own: the sign-up, or
              // '/', which sends an unfinished profile to the onboarding.
              await this._router.navigate(isLoggedIn ? ['/'] : ['/auth/first']);
              return;
            }
          }

          // check later
          // if (organizerId && slug.startsWith('/home/profile/earn-money') && isLoggedIn) {
          //   const currentUserId = await firstValueFrom(this.store.select(UserProfileSelector.userId));
          //   if (currentUserId && currentUserId !== organizerId) {
          //     await this._router.navigate([HOME_PATH, 'explore']);
          //     const alert = await this.alertController.create({
          //       header: 'Wrong account',
          //       message:
          //         'This link belongs to another Smatchy account. Please sign in with the account that received this email.',
          //       buttons: ['OK'],
          //     });
          //     await alert.present();
          //     return;
          //   }
          // }

          if (!isLoggedIn && slug && slug.startsWith('/home/')) {
            // preserve full path + query for post-login redirect
            const search = new URLSearchParams(queryParams).toString();
            const returnUrl = search ? `${slug}?${search}` : slug;
            sessionStorage.setItem('returnUrl', returnUrl);
            await this._router.navigate(['/auth/first']);
            return;
          }

          if (slug.startsWith('/home/')) {
            const navigationPath = slug.split('/').filter((s) => s !== '');
            await this._router.navigate(navigationPath, { queryParams });
          } else if (slug && slug !== '/') {
            const navigationPath = slug.split('/').filter((s) => s !== '');
            await this._router.navigate(navigationPath, { queryParams });
          } else {
            await this._router.navigate([HOME_PATH, 'explore']);
          }
        } catch (error) {
          console.error('Error handling deep link:', error);
        }
      });
    });

    if (Capacitor.isNativePlatform()) {
      await this._pushNotificationAdapter.addActionPerformedListener();
    }

    await this._platform.ready();

    const launchUrl = Capacitor.isNativePlatform() ? await App.getLaunchUrl() : undefined;
    const hasNotificationOpen = Capacitor.isNativePlatform()
      ? await this._pushNotificationAdapter.waitForNotificationOpen(600)
      : false;

    if (
      Capacitor.isNativePlatform() &&
      !launchUrl?.url &&
      !hasNotificationOpen &&
      !this._pushNotificationAdapter.hasRecentNotificationOpen()
    ) {
      /*
       * '/' always. It used to start on /onboarding when a sign-up was pending
       * and there was no token -- the state of a half-created account, which
       * cannot happen now that the account is created as soon as the password is
       * confirmed. A member whose profile is incomplete is routed from '/' by
       * logged.component, wherever they left off.
       */
      await this._navCtrl.navigateRoot('/', {
        animated: false,
        replaceUrl: true,
      });
    }
  }

  private setupBackButtonHandler() {
    this.backButtonSubscription = this._platform.backButton.subscribeWithPriority(10, async () => {
      await this.zone.run(async () => {
        await this.handleBackButton();
      });
    });
  }

  private async handleBackButton() {
    if (await this.hasOpenOverlay()) {
      return;
    }

    if (this._exploreMapUiService.isExpandedMapOpen()) {
      this._exploreMapUiService.requestCloseExpandedMap();
      return;
    }

    if (this._navigationRedirectService.shouldRedirectToExploreOnBack()) {
      await this._navigationRedirectService.handleBackNavigationFromGroupDetail(true);
      return;
    }

    if (this._location.isCurrentPathEqualTo('/home/explore')) {
      await this.exitApp();
    } else {
      this._navCtrl.back();
    }
  }

  private async hasOpenOverlay(): Promise<boolean> {
    const modal = await this._modalCtrl.getTop();
    if (modal) {
      await modal.dismiss();
      return true;
    }

    const alert = await this.alertController.getTop();
    if (alert) {
      await alert.dismiss();
      return true;
    }

    const actionSheet = await this._actionSheetCtrl.getTop();
    if (actionSheet) {
      await actionSheet.dismiss();
      return true;
    }

    return false;
  }

  /**
   * The other half of the gesture people already know.
   *
   * Ionic owns the left edge where it can go back (see setupBackSwipe for
   * where it cannot): dragging from it goes back, with a transition that
   * follows the finger, and `swipeBackEnabled: true` in app.module turns it on
   * for Android too rather than iOS alone. Nothing at all owns the right edge,
   * so going forward again after a step back was impossible by touch.
   *
   * The rule lives in core/navigation/edge-swipe.ts and what lies ahead is
   * counted in NavigationHistoryService, because the browser will not say.
   *
   * IT REFUSES QUIETLY AND ON PURPOSE. When there is nothing ahead the screen
   * does not move and nothing is announced. A gesture that explains itself
   * would interrupt somebody who merely brushed the edge of the phone.
   */
  private setupForwardSwipe(): void {
    const gesture = this._gestureCtrl.create({
      el: document.body,
      gestureName: 'forward-swipe',
      // Above the content, below Ionic's own back gesture, which is 40.
      gesturePriority: 30,
      direction: 'x',
      threshold: 10,
      canStart: (detail) => this._navigationHistory.canGoForward && detail.startX >= window.innerWidth - EDGE_WIDTH_PX,
      onEnd: (detail) => {
        const asksToGoForward = isForwardSwipe({
          startX: detail.startX,
          deltaX: detail.deltaX,
          deltaY: detail.deltaY,
          screenWidth: window.innerWidth,
        });
        if (asksToGoForward) {
          this.zone.run(() => this._navigationHistory.goForward());
        }
      },
    });

    gesture.enable(true);
    this.forwardSwipeGesture = gesture;
  }

  /**
   * The left edge on the screens Ionic's own back gesture cannot reach.
   *
   * WHY IT IS NEEDED. Ionic's gesture belongs to a router outlet and only
   * starts when that outlet's stack can go back. Under `<ion-tabs>` the stack
   * is split by the first segment after /home, and the activity, the member,
   * the group and the other detail pages are declared next to the tabs rather
   * than inside one. To Ionic, opening one is a tab switch: a stack of a
   * single page, nothing to go back to. So the left edge did nothing on most
   * of the application, and the right edge with it, because there is only
   * something ahead after a step back. The wiring spec read the source and
   * passed; ionic-cannot-swipe-back-out-of-a-sibling-of-the-tabs.spec.ts
   * holds the cause against Ionic itself.
   *
   * Below Ionic's 40, so wherever Ionic can go back it keeps the edge and its
   * transition that follows the finger. Here the page changes at the end of
   * the swipe instead. It goes back through the history, like Ionic, so the
   * right edge can come forward again. With nothing behind (a cold start, a
   * notification) it climbs to the parent screen, like the back arrow. On the
   * tab roots it does nothing, as in any iPhone application with tabs.
   */
  private setupBackSwipe(): void {
    const gesture = this._gestureCtrl.create({
      el: document.body,
      gestureName: 'back-swipe',
      gesturePriority: 30,
      direction: 'x',
      threshold: 10,
      canStart: (detail) => detail.startX <= EDGE_WIDTH_PX && !isATabRoot(this._router.url),
      onEnd: (detail) => {
        const asksToGoBack = isBackSwipe({
          startX: detail.startX,
          deltaX: detail.deltaX,
          deltaY: detail.deltaY,
          screenWidth: window.innerWidth,
        });
        if (!asksToGoBack) {
          return;
        }
        this.zone.run(() => {
          if (this._navigationHistory.canGoBack) {
            this._navCtrl.back();
          } else {
            void this._navCtrl.navigateBack(backTarget(this._router.url));
          }
        });
      },
    });

    gesture.enable(true);
    this.backSwipeGesture = gesture;
  }

  ngOnDestroy(): void {
    this.forwardSwipeGesture?.destroy();
    this.backSwipeGesture?.destroy();
    if (this.backButtonSubscription) {
      this.backButtonSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private async _setupOffline() {
    // The screen that says the network is gone has to exist before anybody can
    // ask for it: listening first left a window where losing the network read
    // « dismiss of undefined » and took the application down with it.
    this.offlineModal = await this._modalCtrl.create({
      component: OfflineContainerComponent,
    });
    this.offlineModalIsOpen = false;

    window.addEventListener('online', () => void this.updateNetworkStatus());
    window.addEventListener('offline', () => void this.updateNetworkStatus());

    await this.updateNetworkStatus();
  }

  private async exitApp() {
    const alert = await this.alertController.create({
      header: this.translate.instant('APP.EXIT_APP.TITLE'),
      message: this.translate.instant('APP.EXIT_APP.MESSAGE'),
      buttons: [
        {
          text: this.translate.instant('APP.EXIT_APP.CANCEL'),
          role: 'cancel',
          cssClass: 'align-self-start',
        },
        {
          text: this.translate.instant('APP.EXIT_APP.BUTTON'),
          handler: () => {
            App.exitApp();
          },
          cssClass: 'align-self-end bg-red-50',
          role: 'destructive',
        },
      ],
    });

    await alert.present();
  }

  private async updateNetworkStatus(): Promise<void> {
    if (!this.offlineModal) {
      return;
    }

    // The browser fires these events more than once for the same state, and
    // asking a modal that is not open to close itself is an error of its own.
    // Read with care: this runs from the network listeners, and a throw here
    // is CAPACITOR-B again (see offline-screen.spec.ts).
    const offline = !navigator.onLine && !theScreenSaysItsOwnError(this._router?.url ?? '');
    if (offline === this.offlineModalIsOpen) {
      return;
    }

    this.offlineModalIsOpen = offline;

    try {
      await (offline ? this.offlineModal.present() : this.offlineModal.dismiss());
    } catch {
      // Losing the network must not be a second failure: the screen is a
      // courtesy, and the application keeps running without it.
      this.offlineModalIsOpen = !offline;
    }
  }
}
